import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, FolderKanban, Building2, FileText } from "lucide-react";

const Group = ({ title, icon: Icon, items, render, onPick, testid }) => items.length === 0 ? null : (
  <div data-testid={testid}>
    <div className="overline px-3 pt-2 pb-1 flex items-center gap-1.5"><Icon className="h-3 w-3" />{title}</div>
    {items.map((it) => (
      <button key={it.key} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(it)} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2" data-testid={`search-result-${it.key}`}>{render(it)}</button>
    ))}
  </div>
);

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); ref.current?.focus(); } };
    window.addEventListener("keydown", onKey); return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => setRes(null)), 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { navigate(path); setOpen(false); setQ(""); };
  const projects = (res?.projects || []).map((p) => ({ ...p, key: `project-${p.project_id}` }));
  const customers = (res?.customers || []).map((c) => ({ ...c, key: `customer-${c.customer_id}` }));
  const documents = (res?.documents || []).map((d) => ({ ...d, key: `document-${d.document_id}` }));
  const total = projects.length + customers.length + documents.length;

  return (
    <div className="relative flex-1 max-w-xl" data-testid="global-search">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <Input ref={ref} value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); ref.current?.blur(); } if (e.key === "Enter" && projects[0]) go(`/projects/${projects[0].project_id}`); }}
        placeholder="Search projects, customers, documents…" className="pl-9 h-9 bg-slate-50 border-slate-200" data-testid="topbar-search-input" />
      <kbd className="hidden sm:inline-flex absolute right-2 top-1/2 -translate-y-1/2 items-center gap-1 rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-500 font-mono">⌘ K</kbd>
      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-11 z-40 rounded-lg border border-slate-200 bg-white shadow-xl max-h-[70vh] overflow-y-auto py-1" data-testid="search-results">
          {res && total === 0 && <p className="px-3 py-3 text-sm text-slate-500" data-testid="search-empty">No results for “{q}” in this workspace.</p>}
          {!res && <p className="px-3 py-3 text-sm text-slate-400">Searching…</p>}
          <Group title="Projects" icon={FolderKanban} items={projects} testid="search-group-projects" onPick={(p) => go(`/projects/${p.project_id}`)}
            render={(p) => <><span className="font-mono text-xs bg-slate-900 text-white rounded px-1.5 py-0.5">{p.project_number}</span><span className="truncate">{p.customer_name} / {p.brand}</span>{p.status === "archived" && <span className="text-[10px] bg-slate-200 text-slate-600 rounded px-1.5 py-0.5" data-testid="search-archived-badge">Archived</span>}<span className="ml-auto text-xs text-slate-500">{p.current_phase_name}</span></>} />
          <Group title="Customers" icon={Building2} items={customers} testid="search-group-customers" onPick={(c) => go(`/projects?customer=${encodeURIComponent(c.name)}`)}
            render={(c) => <><span className="font-medium">{c.name}</span><span className="text-xs text-slate-500 truncate">{(c.brands || []).join(", ")}</span>{c.country && <span className="ml-auto text-xs text-slate-500">{c.country}</span>}</>} />
          <Group title="Documents" icon={FileText} items={documents} testid="search-group-documents" onPick={(d) => go(`/projects/${d.project_id}?phase=${d.phase_id || ""}`)}
            render={(d) => <><span className="truncate">{d.file_name}</span>{d.caption && <span className="text-xs text-slate-500 truncate">“{d.caption}”</span>}<span className="ml-auto font-mono text-xs text-slate-500">{d.project_number}</span></>} />
        </div>
      )}
    </div>
  );
};
